import React from 'react';
import loadable from '@loadable/component';

import { compose } from 'redux';
import { connect } from 'react-redux';

import { propTypes } from '../../util/types';

import FallbackPage from './FallbackPage';
import { getDefaultLandingPageData, SectionDefaultLandingHero } from './FallbackPage';
import SectionFaq from '../PageBuilder/SectionBuilder/SectionFaq';
import SectionLandingListingRows from '../PageBuilder/SectionBuilder/SectionLandingListingRows/SectionLandingListingRows';
import SectionLogoSlider from '../PageBuilder/SectionBuilder/SectionLogoSlider';
import SectionLocations from '../PageBuilder/SectionBuilder/SectionLocations';
import SectionSpaceOwner from '../PageBuilder/SectionBuilder/SectionSpaceOwner';
import SectionAirportLounges from '../PageBuilder/SectionBuilder/SectionAirportLounges';
import { fetchFeaturedListings } from '../../ducks/featuredListings.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

/**
 * Landing page always shows the custom Airbnb-style start page
 * (hero + category bar + category shortcuts). Hosted landing-page asset from Console is not used.
 */
export const LandingPageComponent = props => {
  const { error } = props;

  return (
    <PageBuilder
      pageAssetsData={getDefaultLandingPageData()}
      inProgress={false}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      options={{
        sectionComponents: {
          defaultLandingHero: { component: SectionDefaultLandingHero },
          landingListingRows: { component: SectionLandingListingRows },
          logoSlider: { component: SectionLogoSlider },
          airportLounges: { component: SectionAirportLounges },
          sectionLocations: { component: SectionLocations },
          sectionSpaceOwner: { component: SectionSpaceOwner },
          faq: { component: SectionFaq },
        },
      }}
      // No featuredListings prop here on purpose: v10.14.0's featured-listings sections are keyed
      // by the hosted landing-page asset, and this page deliberately ignores that asset (see
      // above) in favour of our own start page. The plumbing is wired up in PageBuilder and
      // CMSPage, so hosted pages can still use the new section type.
    />
  );
};

LandingPageComponent.propTypes = {
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const featuredListingData = state.featuredListings || {};

  const getListingEntitiesById = listingIds => getListingsById(state, listingIds);

  return { pageAssetsData, featuredListingData, getListingEntitiesById, inProgress, error };
};

const mapDispatchToProps = dispatch => ({
  onFetchFeaturedListings: (sectionId, parentPage, listingImageConfig, allSections) =>
    dispatch(fetchFeaturedListings({ sectionId, parentPage, listingImageConfig, allSections })),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(connect(mapStateToProps, mapDispatchToProps))(LandingPageComponent);

export default LandingPage;
